import { Node, Pagination } from '@ekkogmbh/apisdk';
import { Box, Stack, Typography } from '@mui/material';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { GenericDialog } from '../../Common/Components/GenericDialog';
import { DataTable, dataTableHighlight } from '../../Common/Components/DataTable';
import ScrollableStack from 'src/Common/Components/ScrollableStack';
import { NodeSeparator } from '../../Common/Helper/Nodes';
import { injectFakePagination } from '../../Common/Helper/Pagination';
import Permissions from 'src/Common/Permissions';
import { AreaPanel } from './AreaPanel';
import { AnnotationDialog } from 'src/Common/Components/AnnotationDialog';
import {
  Add,
  ArrowBack,
  Assignment,
  Delete,
  Refresh,
  Remove,
  SubdirectoryArrowRight,
  TurnedIn,
  VerticalSplit,
  Warning,
} from '@mui/icons-material';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import { Link } from 'react-router-dom';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import { useContentControl } from 'src/Common/Stores/ContentControlProvider';
import useTaskCollections from 'src/Common/Providers/TaskCollectionProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { CenteredColumn } from 'src/Common/Components/Stacks';

export const nodeSeparator = ' ' + NodeSeparator + ' ';

const ROW_MIN_HEIGHT = 48;

export const AreaManagementContent = (): React.JSX.Element => {
  const { api, hasPermission } = useEkko();
  const { subscribe: subscribeTaskCollection } = useTaskCollections();
  const { refresh } = usePagination();
  const { togglePanel, setPanelOpen } = useContentControl();
  const navigate = useNavigate();
  const routeParams = useParams();

  const [queuePartitions, setQueuePartitions] = useState<string[]>([]);
  const [pathNode, setPathNode] = useState<Node | undefined>();
  const [selectedNode, setSelectedNode] = useState<Node | undefined>();
  const [openDialog, setOpenDialog] = useState<'annotation' | 'sync' | 'delete' | 'partition' | undefined>();

  const [isFetchingPartitions, startFetchingPartitions] = useRequest<string[]>();
  const [, startFetchingNode] = useRequest<Node>();
  const [, startProcessing] = useRequest();

  /** on mount / unmount */
  useEffect(() => {
    if (pathNode) {
      refresh();
    }
  }, [pathNode, api]);

  useEffect(() => {
    const { id } = routeParams;

    if (id === undefined) {
      if (pathNode !== undefined) {
        setPathNode(undefined);
        refresh();
      }
      return;
    }

    const nodeId = parseInt(id, 10);
    if (pathNode?.id === nodeId) {
      return;
    }

    startFetchingNode(
      () => api.getNode(nodeId),
      (n) => setPathNode(n),
    );
  }, [api, routeParams.id]);

  const fetchItems = useCallback(
    async (pagination: Pagination) => {
      let data: Node[];
      if (pathNode === undefined) {
        data = await api.getNodes();
      } else {
        data = await api.getNodeChildren(pathNode.id);
        data.unshift(pathNode);
      }

      return injectFakePagination<Node>(data, pagination);
    },
    [pathNode],
  );

  const fetchQueuePartitions = useCallback(() => {
    if (isFetchingPartitions) {
      return;
    }

    startFetchingPartitions(
      async () => (await api.getQueuePartitions({ limit: 999, page: 1 })).items ?? [],
      (partitions?: string[]) => setQueuePartitions(partitions ?? []),
    );
  }, [api, isFetchingPartitions]);

  const addQueuePartition = useCallback(
    async (coordinate: string) => {
      await api.createQueuePartition(coordinate);
      fetchQueuePartitions();
    },
    [api],
  );

  const removeQueuePartition = useCallback(
    async (coordinate: string) => {
      await api.deleteQueuePartition(coordinate);
      fetchQueuePartitions();
    },
    [api],
  );

  const addNode = useCallback(
    (value: string) => {
      if (pathNode === undefined) {
        return;
      }

      startFetchingNode(
        () => api.addNodeChild(value, pathNode),
        () => {
          onDismiss();
          refresh();
        },
      );
    },
    [pathNode],
  );

  const deleteNode = useCallback((node: Node) => {
    startFetchingNode(() => api.deleteNode(node), refresh);
  }, []);

  const annotateNode = useCallback(
    async (annotations: { system?: Record<string, string>; custom?: Record<string, string> }) => {
      if (selectedNode === undefined) {
        return;
      }

      startFetchingNode(() => api.annotateNode(selectedNode, annotations), refresh);
    },
    [api, selectedNode],
  );

  const onDismiss = () => {
    setSelectedNode(undefined);
    setPanelOpen(false);
    setOpenDialog(undefined);
  };

  const onDeleteOk = useCallback(async () => {
    if (selectedNode) {
      deleteNode(selectedNode);
    }
    onDismiss();
  }, [api, selectedNode, deleteNode]);

  const onSyncOk = useCallback(async () => {
    if (selectedNode === undefined) {
      return;
    }

    startProcessing(() => api.syncCompartmentLinks([], [selectedNode.coordinate], subscribeTaskCollection), onDismiss);
  }, [api, selectedNode]);

  const panel = useMemo(
    () => <AreaPanel node={pathNode} closeHandler={onDismiss} saveHandler={(value) => addNode(value)} />,
    [pathNode],
  );

  const dialog = useMemo(() => {
    if (!selectedNode) {
      return undefined;
    }
    switch (openDialog) {
      case 'delete':
        return (
          <GenericDialog
            open
            centered
            fullWidth
            type="confirmation"
            maxWidth={'sm'}
            title={'Delete Area'}
            text={`Delete Area ${selectedNode.value}?`}
            onClose={onDismiss}
            onConfirm={onDeleteOk}
          />
        );
      case 'annotation':
        return (
          <AnnotationDialog
            open
            editable
            title={'Node Annotations'}
            annotations={selectedNode!.annotations}
            isRemovableFilter={(_key, entry) => {
              if (entry.path === undefined) {
                // system annotation
                return false;
              } else if (entry.path !== selectedNode.path) {
                // inherited annotation
                return `Inherited from Area: "${entry.path}"`;
              } else {
                // own annotation
                return true;
              }
            }}
            systemKeys={['inventoryField', 'ekanbanState', 'fenceName', 'fenceId']}
            onSaveCallback={async (annotations: Record<string, string>) => {
              const system: Record<string, string> = {};
              const custom: Record<string, string> = {};

              Object.keys(annotations).forEach((key: string) => {
                if (key.startsWith('_')) {
                  system[key.substring(1)] = annotations[key];
                } else {
                  custom[key] = annotations[key];
                }
              });

              annotateNode({ custom, system });
            }}
            onClose={onDismiss}
          />
        );
      case 'sync':
        return (
          <GenericDialog
            open
            centered
            fullWidth
            type={'confirmation'}
            maxWidth={'sm'}
            title={'Synchronize linked devices in Area'}
            text={
              <Stack direction={'column'} spacing={2} alignItems={'center'}>
                <Typography variant={'body1'} fontWeight={'bold'}>{`${selectedNode.path}`}</Typography>
                <div>
                  <Warning />
                  <Typography variant={'subtitle1'}>
                    {'Synchronizing areas with many linked devices may take some time'}
                  </Typography>
                </div>
              </Stack>
            }
            onClose={onDismiss}
            onConfirm={onSyncOk}
            okButtonDisabled={false}
          />
        );
      case 'partition':
        const isPartition = queuePartitions.includes(selectedNode.coordinate);
        return (
          <GenericDialog
            open
            centered
            fullWidth
            type={'confirmation'}
            maxWidth={'sm'}
            title={'Queue Partition'}
            text={
              <CenteredColumn>
                <Typography fontWeight={700} variant={'body1'}>{`${selectedNode.coordinate}`}</Typography>
                <Typography fontWeight={500} variant={'body2'} color={isPartition ? 'AccentColor' : 'error'}>
                  {isPartition ? 'ENABLED' : 'DISABLED'}
                </Typography>
              </CenteredColumn>
            }
            onClose={onDismiss}
            onConfirm={async () => {
              if (isPartition) {
                await removeQueuePartition(selectedNode.coordinate);
              } else {
                await addQueuePartition(selectedNode.coordinate);
              }
              onDismiss();
            }}
            buttonText={{ ok: isPartition ? 'disable' : 'enable', cancel: 'cancel' }}
          />
        );
      default:
        return undefined;
    }
  }, [openDialog, queuePartitions, selectedNode]);

  const hasWritePermission = useMemo(() => hasPermission(Permissions.AREAS_WRITE), [hasPermission]);
  const hasUsersPermission = useMemo(() => hasPermission(Permissions.USERS_READ), [hasPermission]);
  const hasRolePermission = useMemo(() => hasPermission(Permissions.MAPPINGS_READ), [hasPermission]);

  const tableButtons = useMemo(() => {
    const btns = [];
    btns.push(
      <ContentControlButton
        key={'add'}
        content={<Add />}
        onClick={togglePanel}
        tooltip={'Add Node'}
        requiredPermission={Permissions.AREAS_WRITE}
        disabled={pathNode === undefined}
      />,
    );

    if (pathNode !== undefined) {
      const parentPath = !!pathNode.parentId ? '/areas/' + pathNode.parentId.toString() : '/areas';
      btns.unshift(
        <ContentControlButton
          key={'pathback'}
          content={<ArrowBack />}
          onClick={() => {
            navigate(parentPath);
          }}
          tooltip={'Back to Parent Node'}
        />,
      );
    }

    return btns;
  }, [pathNode, togglePanel]);

  return (
    <ContentControl panel={panel} dialog={dialog} buttons={tableButtons}>
      <DataTable
        fetchItems={fetchItems}
        maxExpandedActions={3}
        actions={[
          {
            title: 'Queue Partition',
            icon: <VerticalSplit />,
            processing: isFetchingPartitions,
            onClick: (area: Node) => {
              setSelectedNode(area);
              setOpenDialog('partition');
            },
          },
          {
            title: 'Annotations',
            icon: <TurnedIn />,
            onClick: (area: Node) => {
              setSelectedNode(area);
              setOpenDialog('annotation');
            },
          },
          {
            title: 'Users',
            icon: <Assignment />,
            onClick: (area: Node) => {
              navigate('/areas/' + area.id + '/users');
            },
            disabled: !(hasUsersPermission && hasRolePermission),
          },
          {
            title: 'Synchronize Devices',
            icon: <Refresh />,
            onClick: (area: Node) => {
              setSelectedNode(area);
              setOpenDialog('sync');
            },
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (area: Node) => {
              setSelectedNode(area);
              setOpenDialog('delete');
            },
            disabled: !hasWritePermission,
          },
        ]}
        columns={[
          {
            header: 'Name',
            proportionalContent: '25%',
            body: (area: Node, searchText?: string) => {
              const isCurrent = area.id === pathNode?.id;
              const icon = isCurrent ? <ArrowBack /> : area.hasChildren ? <SubdirectoryArrowRight /> : <Remove />;

              const content = (
                <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
                  {icon}
                  <Typography>{dataTableHighlight(area.value, [searchText ?? ''].filter(Boolean))}</Typography>
                </ScrollableStack>
              );

              let linkTo: string;
              if (isCurrent) {
                if (area.parentId === null) {
                  linkTo = '/areas';
                } else {
                  linkTo = '/areas/' + String(area.parentId);
                }
              } else {
                linkTo = '/areas/' + String(area.id);
              }

              return area.hasChildren || isCurrent ? (
                <Box
                  component={Link}
                  to={linkTo}
                  style={{ color: 'primary', textDecoration: 'none' }}
                  sx={{ color: 'inherit' }}
                >
                  {content}
                </Box>
              ) : (
                content
              );
            },
          },
          {
            header: 'Path',
            proportionalContent: '65%',
            body: (area: Node, searchText?: string) => {
              const nodeValues = area.path.split(nodeSeparator);
              return (
                <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
                  {nodeValues.map((val, idx) => (
                    <Fragment key={`${val}${idx}`}>
                      <span style={{ verticalAlign: 'middle' }}>
                        {dataTableHighlight(val, searchText ? [searchText] : undefined)}
                      </span>
                      {idx < nodeValues.length - 1 && <span style={{ verticalAlign: 'middle' }}>{nodeSeparator}</span>}
                    </Fragment>
                  ))}
                </ScrollableStack>
              );
            },
          },
          {
            header: 'Type',
            fitContent: true,
            body: (area: Node) => area.type,
          },
        ]}
      />
    </ContentControl>
  );
};
